import { useEffect, useState } from "react";
import Job from "../../models/Job";
import { DrawerForm } from "../../../../../components/drawers/DrawerForm";
import { useHub } from "../../../../../core/Global";
import { API_ROUTES } from "../../../../../core/Constants";
import TextArea from "../../../../../components/form/TextArea";
import FlexRow from "../../../../../components/base/FlexRow";
import Icon from "../../../../../components/base/Icon";
import JobStatus from "../../../base/models/JobStatus";
import JobCancelReasonField, { CancelReasonValue, isCancelReasonValid, showCancelReasonError } from "./JobCancelReasonField";

const DRAWER_ID = "drawer-update-status-note";

interface IProps {
    job?: Job
    status?: JobStatus
    onClose?: () => void
    onUpdate?: () => void
}

/**
 * Writing the note that goes with a status change — and, when the status is a cancellation,
 * picking the reason for it.
 *
 * A drawer rather than a modal: the modal body is height-capped and scrolls, which cut the cancel
 * reason dropdown off at its bottom edge. The drawer gives the panel the room it needs.
 */
export default function JobUpdateStatusNoteDrawer(props: IProps) {

    const hub = useHub();

    const [job, jobSet] = useState<Job>();
    useEffect(() => jobSet(props.job), [props.job]);

    const [selectedStatus, selectedStatusSet] = useState<JobStatus>();
    useEffect(() => selectedStatusSet(props.status), [props.status]);

    // Only used when the status being set is a cancellation: either a ready-made reason or a note
    // written here. Declared after the hooks above, per the hooks-before-consts rule.
    const [cancelReason, cancelReasonSet] = useState<CancelReasonValue>();

    const canceling = selectedStatus?.isCanceled ?? false;

    return (
        <DrawerForm id={DRAWER_ID} permanent size="medium"
            title={canceling ? "Cancel Job" : "Status"}
            titleIcon={canceling ? "ban" : "circle-dot"}
            subtitle={"Job ID: " + job?.jobId + " | " + job?.fullName}
            buttonText={canceling ? "Cancel the Job" : "Update Status"}
            buttonColor={canceling ? "danger" : "primary"}
            onOpen={() => {
                if (!canceling) $("#txt-status-note-area").focus();
            }}
            onClose={props.onClose}
            onSubmit={(body, callback) => {

                window.hideFormErrors();

                if (canceling) {
                    // A reason off the list counts as the note, so the check is "is there an
                    // answer", not "was something typed".
                    if (!isCancelReasonValid(cancelReason)) {
                        showCancelReasonError(cancelReason, DRAWER_ID);
                        if (callback) callback();
                        return;
                    }

                    hub.Post(API_ROUTES.Jobs + "jobstatus", {
                        id: job?.id,
                        status: selectedStatus?.id,
                        note: cancelReason?.note,
                        cancelReasonId: cancelReason?.cancelReasonId
                    }, res => {
                        if (res.success && props.onUpdate) props.onUpdate();
                        if (callback) callback(res);
                    });

                    return;
                }

                if (body.note == null || body.note == "") {
                    window.showFormError("note", "Please write a note to update the status!", true, DRAWER_ID);
                    if (callback) callback();
                    return;
                }

                hub.Post(API_ROUTES.Jobs + "jobstatus", {
                    ...body,
                    id: job?.id,
                    status: selectedStatus?.id
                }, res => {
                    if (res.success && props.onUpdate) props.onUpdate();
                    if (callback) callback(res);
                });

            }}>

            <div className="mb-6">
                <div className="fs-8 fw-bold text-gray-400 text-uppercase mb-2">
                    New Status
                </div>
                <FlexRow className="rounded px-4 py-3" style={{ backgroundColor: selectedStatus?.color }}>
                    <Icon icon="circle" type="solid" size={10} className="text-white me-2" />
                    <span className="text-white fw-bold fs-7">
                        {selectedStatus?.name}
                    </span>
                </FlexRow>
            </div>

            {
                canceling ?
                    <JobCancelReasonField autoFocusNote onChange={cancelReasonSet} />
                    :
                    <TextArea id="txt-status-note-area" label="Note *" block labelUp name="note" rows={5}
                        placeholder="Please write a note..." />
            }

        </DrawerForm>
    )

}
